import React, { useState, useEffect, useRef } from 'react'
import {
  Timer, CheckCircle, XCircle, ChevronRight,
  RotateCcw, Trophy, AlertTriangle, BookOpen, ChevronDown
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'

const UNIT_ID = 'F1-1'
const PASS_SCORE = 70
const TIME_LIMIT = 25 * 60 // 25분

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string
  concept: string
  misconceptionCode: string
  learnStepId: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '정비례 관계',
    difficulty: 'easy',
    question: 'y가 x에 정비례할 때, 올바른 관계식은 무엇인가요?',
    choices: ['y = ax + b', 'y = ax', 'y = a/x', 'y = x + a'],
    answer: 1,
    explanation: '정비례 관계는 y = ax (a≠0) 형태이며, 반드시 원점을 통과합니다.',
    concept: '정비례 관계식', misconceptionCode: 'FP01', learnStepId: 'FA01',
  },
  {
    id: 'Q02', number: 2, category: '정비례 판별',
    difficulty: 'easy',
    question: 'x=2일 때 y=6, x=4일 때 y=12이다. 이 관계는?',
    choices: ['반비례', 'y=x+4', '정비례', '아무 관계 없음'],
    answer: 2,
    explanation: 'y÷x = 6÷2 = 3, 12÷4 = 3 (일정) → 정비례 y=3x',
    concept: '정비례 판별', misconceptionCode: 'FP01', learnStepId: 'FA01',
  },
  {
    id: 'Q03', number: 3, category: '비례상수',
    difficulty: 'easy',
    question: 'y가 x에 정비례하고 x=5일 때 y=20이다. 비례상수 a는?',
    choices: ['2', '3', '4', '5'],
    answer: 2,
    explanation: 'a = y÷x = 20÷5 = 4. 따라서 y=4x',
    concept: '비례상수 구하기', misconceptionCode: 'FP01', learnStepId: 'FA01',
  },
  {
    id: 'Q04', number: 4, category: '정비례 활용',
    difficulty: 'easy',
    question: '사탕 한 개에 300원이다. x개를 살 때 금액 y원의 관계식은?',
    choices: ['y = x + 300', 'y = 300/x', 'y = 300x', 'y = 3x'],
    answer: 2,
    explanation: '단가×수량=금액 → y = 300x (정비례)',
    concept: '정비례 활용', misconceptionCode: 'FP01', learnStepId: 'FA01',
  },
  {
    id: 'Q05', number: 5, category: '정비례 그래프',
    difficulty: 'easy',
    question: 'y=3x의 그래프는 어느 사분면을 지나는가?',
    choices: ['1·2사분면', '2·4사분면', '1·3사분면', '3·4사분면'],
    answer: 2,
    explanation: 'a=3>0이므로 오른쪽 위(↗) 방향. 1사분면·3사분면 통과.',
    concept: '정비례 그래프 사분면', misconceptionCode: 'FP01', learnStepId: 'FA02',
  },
  {
    id: 'Q06', number: 6, category: '정비례 그래프',
    difficulty: 'medium',
    question: 'y=-4x의 그래프가 지나는 사분면은?',
    choices: ['1·3사분면', '2·4사분면', '1·2사분면', '3·4사분면'],
    answer: 1,
    explanation: 'a=-4<0이므로 오른쪽 아래(↘) 방향. 2사분면·4사분면 통과.',
    concept: '정비례 그래프 사분면(a<0)', misconceptionCode: 'FP01', learnStepId: 'FA02',
  },
  {
    id: 'Q07', number: 7, category: '정비례 그래프',
    difficulty: 'medium',
    question: '다음 중 y축에 가장 가까운(가파른) 그래프는?',
    choices: ['y=½x', 'y=2x', 'y=-3x', 'y=¼x'],
    answer: 2,
    explanation: '|a| 값: ①0.5 ②2 ③3 ④0.25. |a|=3이 가장 크므로 y=-3x가 y축에 가장 가까움.',
    concept: '정비례 그래프 기울기', misconceptionCode: 'FP01', learnStepId: 'FA02',
  },
  {
    id: 'Q08', number: 8, category: '반비례 관계',
    difficulty: 'medium',
    question: 'y가 x에 반비례할 때, x×y의 값은?',
    choices: ['항상 변한다', '0이다', '항상 일정하다', 'x에 따라 다르다'],
    answer: 2,
    explanation: '반비례: y=a/x → xy=a (항상 일정). 이것이 반비례의 핵심 특징!',
    concept: '반비례 특징', misconceptionCode: 'FP02', learnStepId: 'FA03',
  },
  {
    id: 'Q09', number: 9, category: '반비례 비례상수',
    difficulty: 'medium',
    question: 'y가 x에 반비례하고 x=4, y=9일 때 비례상수는?',
    choices: ['2.25', '13', '36', '5'],
    answer: 2,
    explanation: '반비례 비례상수 a = x×y = 4×9 = 36. 따라서 y=36/x',
    concept: '반비례 비례상수', misconceptionCode: 'FP02', learnStepId: 'FA03',
  },
  {
    id: 'Q10', number: 10, category: '반비례 활용',
    difficulty: 'medium',
    question: '일정 거리 180km를 달릴 때, 속도 x(km/h)와 시간 y(h)의 관계식은?',
    choices: ['y=180x', 'y=x+180', 'y=180/x', 'y=x/180'],
    answer: 2,
    explanation: '속도×시간=거리(일정): xy=180 → y=180/x (반비례)',
    concept: '반비례 활용', misconceptionCode: 'FP02', learnStepId: 'FA03',
  },
  {
    id: 'Q11', number: 11, category: '반비례 그래프',
    difficulty: 'medium',
    question: 'y=6/x의 그래프에 대한 설명으로 옳은 것은?',
    choices: ['원점을 지나는 직선이다', '1·3사분면에 있는 쌍곡선이다', '2·4사분면에 있는 쌍곡선이다', 'x축과 한 점에서 만난다'],
    answer: 1,
    explanation: 'a=6>0: 1·3사분면 쌍곡선. 원점을 지나지 않고, 축과 교점 없음.',
    concept: '반비례 그래프 특징', misconceptionCode: 'FP02', learnStepId: 'FA04',
  },
  {
    id: 'Q12', number: 12, category: '반비례 그래프',
    difficulty: 'hard',
    question: 'y=-8/x의 그래프가 지나는 사분면은?',
    choices: ['1·3사분면', '2·4사분면', '1·2사분면', '3·4사분면'],
    answer: 1,
    explanation: 'a=-8<0이므로 2사분면·4사분면의 쌍곡선.',
    concept: '반비례 그래프 사분면(a<0)', misconceptionCode: 'FP02', learnStepId: 'FA04',
  },
  {
    id: 'Q13', number: 13, category: '정비례·반비례 판별',
    difficulty: 'hard',
    question: '다음 중 반비례 관계인 것은?\n(단, a는 0이 아닌 상수)',
    choices: ['넓이가 일정한 삼각형에서 밑변x와 높이y', '단가가 일정할 때 수량x와 금액y', '속도가 일정할 때 시간x와 거리y', '저금통에 매월 같은 금액을 넣을 때 개월x와 총액y'],
    answer: 0,
    explanation: '① 밑변×높이÷2=넓이(일정) → xy=2×넓이 (일정) → 반비례 ✓\n②③④는 모두 정비례 관계.',
    concept: '정비례·반비례 판별', misconceptionCode: 'FP02', learnStepId: 'FA04',
  },
  {
    id: 'Q14', number: 14, category: '정비례 활용',
    difficulty: 'hard',
    question: '물을 1분에 4L씩 채울 때, 40L를 채우는 데 걸리는 시간은?\n(y=4x에서 x의 값)',
    choices: ['8분', '10분', '12분', '160분'],
    answer: 1,
    explanation: 'y=4x에 y=40 대입: 40=4x → x=10 (분)',
    concept: '정비례 방정식 활용', misconceptionCode: 'FP01', learnStepId: 'FA01',
  },
  {
    id: 'Q15', number: 15, category: '반비례 활용',
    difficulty: 'hard',
    question: '6명이 10일에 끝낼 일을 4명이 하면 며칠이 걸리는가?\n(인원×일수=일정)',
    choices: ['12일', '15일', '18일', '20일'],
    answer: 1,
    explanation: '6×10=60 (일정) → y=60/x\nx=4: y=60/4=15 (일)',
    concept: '반비례 활용 문제', misconceptionCode: 'FP02', learnStepId: 'FA04',
  },
]

const concept: { misconceptionCode: string; learnStepId: string } = {
  misconceptionCode: 'FP01', learnStepId: 'FA01'
}

type Phase = 'ready' | 'testing' | 'done'

export default function F1_1TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('ready')
  const [answers, setAnswers] = useState<(number | null)[]>(TEST_QUESTIONS.map(() => null))
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT)
  const [current, setCurrent] = useState(0)
  const [showExp, setShowExp] = useState<Record<string, boolean>>({})
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))

  useEffect(() => {
    if (phase === 'testing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            clearInterval(timerRef.current!)
            finishTest()
            return 0
          }
          return t - 1
        })
      }, 1000)
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [phase])

  const finishTest = () => {
    if (timerRef.current) clearInterval(timerRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const elapsed = TIME_LIMIT - timeLeft
    const wrongCodes = [...new Set(
      TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.misconceptionCode)
    )]
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: elapsed,
      answers: answers.map(a => a ?? -1),
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.learnStepId),
      grade: score >= 90 ? 'A' : score >= 80 ? 'B' : score >= 70 ? 'C' : score >= 60 ? 'D' : 'F',
      passed: score >= PASS_SCORE,
    })
    setPhase('done')
  }

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60).toString().padStart(2, '0')
    const s = (sec % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correct / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE

  const difficultyLabel = { easy: '기초', medium: '중급', hard: '심화' }
  const difficultyColor = {
    easy: 'bg-green-100 text-green-700',
    medium: 'bg-yellow-100 text-yellow-700',
    hard: 'bg-red-100 text-red-700',
  }

  // ── 준비 화면 ──────────────────────────────────────────────────────────────
  if (phase === 'ready') {
    const stepGroups = [
      { id: 'FA01', label: '함수의 뜻과 정비례', count: TEST_QUESTIONS.filter(q => q.learnStepId === 'FA01').length },
      { id: 'FA02', label: '정비례 그래프', count: TEST_QUESTIONS.filter(q => q.learnStepId === 'FA02').length },
      { id: 'FA03', label: '반비례 관계', count: TEST_QUESTIONS.filter(q => q.learnStepId === 'FA03').length },
      { id: 'FA04', label: '반비례 그래프와 활용', count: TEST_QUESTIONS.filter(q => q.learnStepId === 'FA04').length },
    ]
    return (
      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-sky-600 rounded-2xl p-5 text-white text-center">
          <div className="text-4xl mb-2">📝</div>
          <h2 className="text-xl font-black mb-1">정비례와 반비례 실전 TEST</h2>
          <p className="text-sm text-blue-100">F1-1 · 함수 영역</p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {[
            { label: '총 문항', value: `${TEST_QUESTIONS.length}문항` },
            { label: '제한 시간', value: '25분' },
            { label: '합격 기준', value: `${PASS_SCORE}점 이상` },
            { label: '난이도', value: '기초~심화' },
          ].map((item, i) => (
            <div key={i} className="card bg-slate-50 border border-slate-200 text-center py-3">
              <p className="text-xs text-slate-500">{item.label}</p>
              <p className="text-base font-black text-slate-800">{item.value}</p>
            </div>
          ))}
        </div>

        <div className="card border border-slate-200">
          <p className="text-xs font-bold text-slate-600 mb-3">📚 단계별 문항 구성</p>
          <div className="space-y-2">
            {stepGroups.map(g => (
              <div key={g.id} className="flex items-center gap-3">
                <span className="text-xs font-mono font-bold text-blue-600 w-10">{g.id}</span>
                <span className="flex-1 text-xs text-slate-700">{g.label}</span>
                <span className="text-xs font-bold text-slate-500">{g.count}문항</span>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => {
            setPhase('testing')
            setAnswers(TEST_QUESTIONS.map(() => null))
            setTimeLeft(TIME_LIMIT)
            setCurrent(0)
          }}
          className="w-full py-4 bg-gradient-to-r from-blue-500 to-sky-600 text-white font-black rounded-2xl text-lg hover:opacity-90 transition-opacity">
          TEST 시작
        </button>
      </div>
    )
  }

  // ── 시험 화면 ──────────────────────────────────────────────────────────────
  if (phase === 'testing') {
    const q = TEST_QUESTIONS[current]
    const answered = answers.filter(a => a !== null).length
    return (
      <div className="flex flex-col h-full">
        {/* 상단 바 */}
        <div className="px-4 pt-3 pb-2 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-700">{current + 1}/{TEST_QUESTIONS.length}</span>
            <div className={`flex items-center gap-1 font-mono font-bold text-sm px-3 py-1 rounded-full ${timeLeft < 300 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
              <Timer size={13} /> {formatTime(timeLeft)}
            </div>
            <span className="text-xs text-slate-500">답변 {answered}/{TEST_QUESTIONS.length}</span>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-1.5 bg-blue-500 rounded-full transition-all"
              style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
          </div>
        </div>

        {/* 문제 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          <div className="card border border-slate-200">
            <div className="flex items-center gap-2 mb-3">
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${difficultyColor[q.difficulty]}`}>
                {difficultyLabel[q.difficulty]}
              </span>
              <span className="text-xs text-slate-500">{q.category}</span>
            </div>
            <p className="text-sm font-semibold text-slate-800 whitespace-pre-line leading-relaxed">
              {q.number}. {q.question}
            </p>
          </div>

          <div className="space-y-2">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => {
                  const newAnswers = [...answers]
                  newAnswers[current] = i
                  setAnswers(newAnswers)
                }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all text-sm font-medium
                  ${answers[current] === i
                    ? 'border-blue-500 bg-blue-50 text-blue-800'
                    : 'border-slate-200 bg-white hover:border-blue-300 text-slate-700'}`}>
                <span className="font-bold text-blue-500 mr-2">{['①', '②', '③', '④'][i]}</span>
                {choice}
              </button>
            ))}
          </div>

          {/* 이전/다음 */}
          <div className="flex gap-2">
            <button onClick={() => setCurrent(Math.max(0, current - 1))}
              disabled={current === 0}
              className="flex-1 py-3 border border-slate-200 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-colors">
              ← 이전
            </button>
            {current < TEST_QUESTIONS.length - 1 ? (
              <button onClick={() => setCurrent(current + 1)}
                className="flex-1 py-3 bg-blue-500 text-white rounded-xl text-sm font-bold hover:bg-blue-600 transition-colors">
                다음 →
              </button>
            ) : (
              <button onClick={finishTest}
                className="flex-1 py-3 bg-emerald-500 text-white rounded-xl text-sm font-bold hover:bg-emerald-600 transition-colors">
                제출 ({answered}/{TEST_QUESTIONS.length}답)
              </button>
            )}
          </div>

          {/* 빠른 이동 */}
          <div className="flex flex-wrap gap-1.5">
            {TEST_QUESTIONS.map((_, i) => (
              <button key={i} onClick={() => setCurrent(i)}
                className={`w-8 h-8 rounded-lg text-xs font-bold transition-colors
                  ${i === current ? 'bg-blue-500 text-white' : answers[i] !== null ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ── 결과 화면 ──────────────────────────────────────────────────────────────
  const categoryGroups = [
    { label: '정비례 관계·활용', ids: ['FA01'], color: 'bg-blue-500' },
    { label: '정비례 그래프', ids: ['FA02'], color: 'bg-sky-500' },
    { label: '반비례 관계·활용', ids: ['FA03'], color: 'bg-violet-500' },
    { label: '반비례 그래프', ids: ['FA04'], color: 'bg-rose-500' },
  ]

  return (
    <div className="p-4 space-y-4">
      {/* 결과 헤더 */}
      <div className={`rounded-2xl p-5 text-white text-center ${passed ? 'bg-gradient-to-r from-emerald-500 to-teal-600' : 'bg-gradient-to-r from-slate-500 to-slate-600'}`}>
        <div className="text-4xl mb-2">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-xl font-black mb-1">{passed ? '합격!' : '불합격'}</h2>
        <p className="text-4xl font-black mb-1">{score}점</p>
        <p className="text-sm text-white/80">{correct}/{TEST_QUESTIONS.length}문항 정답</p>
        <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-bold ${passed ? 'bg-white/20' : 'bg-white/20'}`}>
          {passed ? '✅ PASS' : `❌ FAIL (합격 기준: ${PASS_SCORE}점)`}
        </span>
      </div>

      {/* 영역별 성적 */}
      <div className="card border border-slate-200">
        <p className="text-xs font-bold text-slate-600 mb-3">📊 영역별 성적</p>
        <div className="space-y-2">
          {categoryGroups.map(g => {
            const groupQs = TEST_QUESTIONS.filter(q => g.ids.includes(q.learnStepId))
            const groupCorrect = groupQs.filter((q, i) => answers[TEST_QUESTIONS.indexOf(q)] === q.answer).length
            const groupPct = groupQs.length > 0 ? Math.round((groupCorrect / groupQs.length) * 100) : 0
            return (
              <div key={g.label}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-semibold text-slate-700">{g.label}</span>
                  <span className="font-bold text-slate-600">{groupCorrect}/{groupQs.length} ({groupPct}%)</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-2 ${g.color} rounded-full transition-all`}
                    style={{ width: `${groupPct}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* 문항별 결과 */}
      <div className="space-y-2">
        <p className="text-sm font-bold text-slate-700">문항별 결과</p>
        {TEST_QUESTIONS.map((q, i) => {
          const isCorrect = answers[i] === q.answer
          const open = showExp[q.id]
          return (
            <div key={q.id} className={`rounded-xl border overflow-hidden ${isCorrect ? 'border-emerald-200' : 'border-red-200'}`}>
              <button
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${isCorrect ? 'bg-emerald-50' : 'bg-red-50'}`}
                onClick={() => setShowExp(p => ({ ...p, [q.id]: !open }))}>
                {isCorrect ? <CheckCircle size={15} className="text-emerald-500 flex-shrink-0" /> : <XCircle size={15} className="text-red-500 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-700">{q.number}번</span>
                    <span className="text-xs text-slate-500">{q.category}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${difficultyColor[q.difficulty]}`}>
                      {difficultyLabel[q.difficulty]}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 truncate">{q.question.slice(0, 30)}...</p>
                </div>
                <ChevronDown size={13} className={`text-slate-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} />
              </button>
              {open && (
                <div className="px-4 py-3 bg-white space-y-2 border-t border-slate-100">
                  <p className="text-sm text-slate-800 whitespace-pre-line">{q.question}</p>
                  {!isCorrect && answers[i] !== null && (
                    <p className="text-xs text-red-600">
                      내 답: {q.choices[answers[i]!]}
                    </p>
                  )}
                  <p className="text-xs text-emerald-700 font-semibold">
                    정답: {q.choices[q.answer]}
                  </p>
                  <p className="text-xs text-slate-500 whitespace-pre-line">{q.explanation}</p>
                  {!isCorrect && onGoLearn && (
                    <button
                      onClick={() => onGoLearn(q.learnStepId)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-semibold mt-1">
                      <BookOpen size={11} /> 개념 다시 학습하기
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 오개념 가이드 */}
      {!passed && (
        <div className="card bg-amber-50 border border-amber-200">
          <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-2">
            <AlertTriangle size={13} /> 오개념 가이드
          </p>
          {[...new Set(TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.misconceptionCode))].map(code => (
            <div key={code} className="mb-2 p-2 bg-white rounded-lg border border-amber-100">
              <p className="text-xs font-bold text-amber-800">{code}</p>
              <p className="text-xs text-amber-700 mt-0.5">
                {code === 'FP01'
                  ? '정비례(y=ax)와 일차함수(y=ax+b)를 혼동하지 마세요. 정비례는 반드시 원점(0,0) 통과!'
                  : '반비례의 비례상수는 a=xy, 정비례는 a=y/x. 판별할 때 표를 만들어 확인하세요!'}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* 완료 메시지 */}
      {passed && (
        <div className="card bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-300 text-center">
          <div className="text-3xl mb-2">🎉</div>
          <p className="text-sm font-bold text-blue-700">정비례와 반비례 완료!</p>
          <p className="text-xs text-slate-500 mt-1">함수 단원의 기초를 완성했습니다</p>
        </div>
      )}

      {/* 다시 풀기 */}
      <button
        onClick={() => {
          setPhase('ready')
          setAnswers(TEST_QUESTIONS.map(() => null))
          setTimeLeft(TIME_LIMIT)
          setCurrent(0)
          setShowExp({})
        }}
        className="w-full py-3 border-2 border-slate-200 text-slate-600 font-bold rounded-2xl flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
        <RotateCcw size={15} /> 다시 풀기
      </button>
    </div>
  )
}
